import { FacebookShareButton, TwitterShareButton } from 'react-share'
import copy from 'copy-to-clipboard'
import { StatType } from '@loc/server/enums/music'
import { useLingui } from '@lingui/react/macro'

import useUpdateLikeStatus from './useUpdateLikeStatus'
import { CheckedSvg } from './CheckedSvg'
import { UnCheckedSvg } from './UnCheckedSvg'

import type { MusicOption, MusicOptionKey } from '../_types'
import type { AudioGlobalMetadata } from '@/hooks/audio/store'

import { saveMap3 } from '@/helpers/download.client'
import { useAntdContext } from '@/contexts/AntdContext/hooks'
import { useMusicLocation } from '@/pages/tad.ai/_hooks/music'
import { useAnalyticsData } from '@/hooks/analyticsData'
import { useSiteSpecificContext } from '@/contexts/SiteSpecificContext'
import { useAppUIModal } from '@/contexts/AppUIModalContext'


import { useConfig } from '@/contexts/SiteContext/hooks';
import { api } from '@/utils/api';
import { CssIcon } from '@/components/CssIcon';
import { cls } from 'tagged-classnames-free';
export interface MusicOptionActions {
  onDeleted?: () => void
  onSelect?: (id: string) => void
}
export interface UseMusicOptionsConfig extends MusicOptionActions {
  data: AudioGlobalMetadata
  hidden?: MusicOptionKey[]
}

export function useMusicOptions(options: UseMusicOptionsConfig) {
  const { data, hidden, onDeleted, onSelect } = options
  const { message, modalInstance } = useAntdContext()
  const { t } = useLingui()
  const musicLocation = useMusicLocation(data.id)
  const config = useConfig()
  const { isPaidUser } = useSiteSpecificContext()
  const { upgradePlan } = useAppUIModal()
  const { mutateAsync: removeAsync } = api.music.remove.useMutation()
  const { trackUserActivityDebounce } = useAnalyticsData()

  const track = (type: StatType) => {
    trackUserActivityDebounce([
      {
        uuid: data.id,
        statType: type,
      },
    ])
  }

  const { likeStatus, handleClickLike } = useUpdateLikeStatus(data)
  const needDownloadUpgrade = !isPaidUser
  const downloadIcon = needDownloadUpgrade ? (
    <span className='relative inline-flex size-5 items-center justify-center'>
      <CssIcon className='i-cus--tad-download size-5' />
      <CssIcon className='i-cus--tad-member-plus text-f-primary absolute -right-1 -top-1.5 size-3' />
    </span>
  ) : (
    cls`i-cus--tad-download`
  )
  const handleDownload = () => {
    if (needDownloadUpgrade) {
      upgradePlan.show({
        reason: t`Please Upgrade to Download Generated Music`,
      })
      return
    }

    saveMap3(data.src, data.title || t`Untitled`)
    track(StatType.Download)
  }

  const handleSelect = (e: React.MouseEvent<HTMLDivElement>) => {
    e.stopPropagation()
    onSelect?.(data.id)
  }
  const musicOptions = (
    [
      {
        key: 'delete',
        label: t`Delete`,
        icon: cls`i-cus--tad-deleted`,
        onClick: () => {
          modalInstance.confirm({
            title: t`Delete Music`,
            content: t`Are you sure you want to delete this music? This action cannot be undone.`,
            okText: t`Delete`,
            cancelText: t`Cancel`,
            onOk: async () => {
              await removeAsync({ uuids: [data.id] })
              onDeleted?.()
            },
          })
        },
      },
      {
        key: 'download',
        label: t`Download`,
        icon: downloadIcon,
        onClick: handleDownload,
      },
      {
        key: 'share',
        label: t`Share`,
        icon: cls`i-cus--tad-share-line`,
        children: [
          {
            key: 'link',
            label: t`Copy Link`,
            icon: cls`i-cus--tad-link`,
            onClick: () => {
              const result = copy(musicLocation.href)
              if (result) {
                message.success(t`Music link copied.`)
              } else {
                message.error(t`Failed to copy music link.`)
              }
            },
          },
          {
            key: 'x',
            label: (
              <TwitterShareButton
                title={config.seoTitle(data.title || t`Untitled`)}
                url={musicLocation.href}
              >
                {t`Share to X`}
              </TwitterShareButton>
            ),
            icon: cls`i-cus--tad-twitter`,
            onClick: () => {
              track(StatType.Share)
            },
          },
          {
            key: 'facebook',
            label: (
              <FacebookShareButton url={musicLocation.href}>
                {t`Share to Facebook`}
              </FacebookShareButton>
            ),
            icon: cls`i-cus--tad-facebook`,
            onClick: () => {
              track(StatType.Share)
            },
          },
        ],
      },
      {
        key: 'liked',
        hidden: data.favoriteNum === undefined, //非success状态的歌曲不显示like按钮
        label: likeStatus.favoriteNum,
        icon: likeStatus.isLiked
          ? cls`i-cus--tad-heart-bold`
          : cls`i-cus--tad-heart`,
        btnClassName: cls`h-auto w-10 flex-col gap-1 border-0 !bg-transparent p-0 ${likeStatus.isLiked ? 'text-f-text-secondary' : 'text-f-text-quaternary'}`,
        onClick: handleClickLike,
      },
      {
        key: 'select',
        label: '',
        icon: data.isSelected ? (
          <CheckedSvg onClick={(e) => handleSelect(e)} />
        ) : (
          <UnCheckedSvg onClick={(e) => handleSelect(e)} />
        ),
      },
    ] satisfies MusicOption[]
  ).filter((item) => {
    if (item.hidden) return false
    if (hidden && hidden.length) {
      return (hidden as string[]).includes(item.key) === false
    }
    return true
  })

  return musicOptions
}
